/**
 * observed-at.ts: how an "as of" time reads to the person looking at it.
 *
 * Until 2026-10-07 every stamp was printed in the MARKET's zone with the zone
 * named ("Amazon price as of Oct 4 at 06:40 PM EDT"), so the server and the
 * browser printed the same string. A reader in Paris or Tokyo had to do the
 * arithmetic. The browser now prints the time in the READER's zone, with no
 * zone label, and says "today" / "yesterday" / "tomorrow" in the reader's own
 * calendar: "Amazon price · today, 6:40 PM".
 *
 * The server cannot know the reader's zone (reading it from a request header
 * would opt a cached page out of caching), so the server render and the
 * no-JavaScript fallback keep `formatAbsoluteObservedAt` (market zone, zone
 * named, which is true for any reader), and `formatObservedStamp` picks one
 * with `viewerLocal`, which `useViewerClock` flips to true only after
 * hydration. A relative word in cached HTML would go stale, so the server
 * string never carries one.
 *
 * Every word that is not the label comes from `Intl`: the day words
 * (RelativeTimeFormat), the clock (a locale's own short time: "6:40 PM",
 * "18:40", "18 h 40"), the date and the word between a date and a clock ("at",
 * "à", ", "). Only the label ("Amazon price") is ours, so a language needs one
 * hand-set string rather than one per day word.
 *
 * Pure and total: never throws, '' for an unparseable input. `now` and
 * `timeZone` are injectable for tests.
 */

import type { RegionCode } from 'shared/src/types/region.type';
import { formatAbsoluteObservedAt } from './offers';

const formatters = new Map<string, Intl.DateTimeFormat>();
const dateTimeFormat = (tag: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${tag}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(tag, options);
    formatters.set(key, formatter);
  }
  return formatter;
};

let cachedViewerZone: string | undefined;
/** The reader's IANA zone, or UTC where the runtime does not say. */
const viewerZone = (): string => {
  if (cachedViewerZone === undefined) {
    try {
      cachedViewerZone = new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      cachedViewerZone = 'UTC';
    }
  }
  return cachedViewerZone;
};

const parseMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

type CalendarDay = { year: number; month: number; day: number };

/** The calendar day an instant falls on in `timeZone`. */
const calendarDay = (ms: number, timeZone: string): CalendarDay => {
  const parts = dateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));
  const part = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day') };
};

const dayNumber = ({ year, month, day }: CalendarDay): number => Date.UTC(year, month - 1, day) / 86_400_000;

const connectors = new Map<string, string>();
/**
 * The locale's own word between a date and a clock ("at", "à", ", "), read off
 * a reference date so no language needs a string here. Only a pure separator
 * counts: Japanese prints "10月4日 18:40", whose literal "日 " is the day's
 * suffix, so it gets a plain space.
 */
const connectorFor = (tag: string): string => {
  const known = connectors.get(tag);
  if (known !== undefined) return known;
  let connector = ' ';
  try {
    const parts = dateTimeFormat(tag, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    }).formatToParts(new Date(Date.UTC(2026, 9, 4, 18, 40)));
    const hour = parts.findIndex((p) => p.type === 'hour');
    const between = parts[hour - 1];
    const dateBefore = parts[hour - 2];
    if (
      between?.type === 'literal' &&
      dateBefore &&
      ['month', 'day', 'weekday', 'year'].includes(dateBefore.type) &&
      /^[\s,;·.  ]/.test(between.value)
    ) {
      connector = between.value;
    }
  } catch {
    // An unknown tag: the plain space stands.
  }
  connectors.set(tag, connector);
  return connector;
};

export type ObservedAtOptions = {
  /** The instant the reader is looking at it. Default: the clock. */
  now?: number;
  /** IANA zone to read the calendar in. Default: the reader's own. */
  timeZone?: string;
};

/**
 * "today, 6:40 PM" / "yesterday, 6:40 PM" / "tomorrow, 6:40 PM" (a deal's end)
 * / "Oct 2, 6:40 PM" / "Oct 2, 2025, 6:40 PM", in the reader's zone and
 * language. The connector between the day and the clock is the browser's own
 * ("today at 6:40 PM" where its locale data says "at"). A language `Intl` has
 * no day words for gets the date form for every day. Falls back to English,
 * then to ''.
 */
export const formatObservedAtLocal = (
  iso: string | null | undefined,
  languageTag: string,
  { now = Date.now(), timeZone }: ObservedAtOptions = {},
): string => {
  const ms = parseMs(iso);
  if (ms === null) return '';
  const zone = timeZone ?? viewerZone();
  for (const tag of [languageTag, 'en']) {
    try {
      const clock = dateTimeFormat(tag, { timeStyle: 'short', timeZone: zone }).format(ms);
      const today = calendarDay(now, zone);
      const observed = calendarDay(ms, zone);
      const daysAgo = dayNumber(today) - dayNumber(observed);
      if (daysAgo >= -1 && daysAgo <= 1 && Intl.RelativeTimeFormat.supportedLocalesOf([tag]).length > 0) {
        const word = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }).format(-daysAgo, 'day');
        return `${word}${connectorFor(tag)}${clock}`;
      }
      const date = dateTimeFormat(tag, {
        month: 'short',
        day: 'numeric',
        ...(today.year === observed.year ? {} : { year: 'numeric' }),
        timeZone: zone,
      }).format(ms);
      return `${date}${connectorFor(tag)}${clock}`;
    } catch {
      // A bad tag or an unknown zone: try the next rung.
    }
  }
  return '';
};

/** The exact instant, for a hover title: "Saturday, October 4, 2026 at 6:40:12 PM EDT". */
export const formatObservedAtFull = (iso: string | null | undefined, languageTag: string): string => {
  const ms = parseMs(iso);
  if (ms === null) return '';
  for (const tag of [languageTag, 'en']) {
    try {
      return dateTimeFormat(tag, { dateStyle: 'full', timeStyle: 'long', timeZone: viewerZone() }).format(ms);
    } catch {
      // Next rung.
    }
  }
  return '';
};

/**
 * The time a stamp prints. `viewerLocal` false (the server render, the
 * hydration pass, no JavaScript): the market's zone, zone named, no relative
 * word. True (the browser, after hydration): the reader's own.
 */
export const formatObservedStamp = (
  iso: string | null | undefined,
  languageTag: string,
  regionCode: RegionCode,
  viewerLocal: boolean,
  now: number = Date.now(),
): string =>
  viewerLocal
    ? formatObservedAtLocal(iso, languageTag, { now }) ||
      formatAbsoluteObservedAt(iso, languageTag, regionCode)
    : formatAbsoluteObservedAt(iso, languageTag, regionCode);
